import { useState, useMemo, useCallback } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { 
  Dialog, DialogContent, DialogDescription, DialogFooter, 
  DialogHeader, DialogTitle, DialogTrigger 
} from "@/components/ui/dialog";
import { toast } from "sonner";
import { ScrollableTable, Column, SortConfig } from "@/components/ui/scrollable-table";

import { useCollection } from "@/hooks/firestore";
import { 
  useAddDocument, 
  useUpdateDocument, 
  // useDeleteDocument // No longer used directly
} from "@/hooks/firestore/operations";
import { usePermissions } from "@/hooks/usePermissions";
import { Attribute } from "@/types/firestore";

// Redux imports for deletion dialog
import { useAppDispatch, useAppSelector } from "@/store/hooks";
import { DeleteDialog } from "../editor/dialogs/DeleteConfirmDialog";
import { 
    EditableEntityType,
    startDeletionAssessment, 
    assessDeletionImpact, 
    deleteEntity, 
    cancelInteraction,
    selectEditorInteractionState,
    selectSelectedEntity,
    selectDeletionImpact,
    selectEditorOperationLoading,
    selectEditorOperationError
} from "@/store/slices/editorSlice";

export const AttributeManager = () => {
  const dispatch = useAppDispatch();
  const { 
    documents: attributes, 
    loading: collectionLoading, 
  } = useCollection<Attribute>('attributes');
  
  // Instantiate individual operation hooks
  const { addDocument, loading: adding, error: addError } = useAddDocument<Attribute>('attributes');
  const { updateDocument, loading: updating, error: updateError } = useUpdateDocument<Attribute>('attributes');
  // const { deleteDocument, loading: deleting, error: deleteError } = useDeleteDocument('attributes'); // Replaced

  // Editor slice selectors
  const interactionState = useAppSelector(selectEditorInteractionState);
  const selectedEntity = useAppSelector(selectSelectedEntity);
  const deletionImpact = useAppSelector(selectDeletionImpact);
  const operationLoading = useAppSelector(selectEditorOperationLoading);
  const operationError = useAppSelector(selectEditorOperationError);
  
  const { hasPermission } = usePermissions();
  const canManage = hasPermission('manage_attributes') || hasPermission('admin');
  
  const [isAddDialogOpen, setIsAddDialogOpen] = useState(false);
  const [editingAttribute, setEditingAttribute] = useState<Attribute | null>(null);
  const [newAttribute, setNewAttribute] = useState({ name: "", groupId: 1, orderInGroup: 1 });
  const [filterGroup, setFilterGroup] = useState<number | null>(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [sortColumn, setSortColumn] = useState<string | null>('groupId');
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('asc');

  // Calculate attribute groups from the attributes collection
  const attributesByGroup = useMemo(() => {
    if (!attributes) return {};
    return attributes.reduce<Record<number, Attribute[]>>((groups, attribute) => {
      const groupId = attribute.groupId || 0;
      if (!groups[groupId]) {
        groups[groupId] = [];
      }
      groups[groupId].push(attribute);
      // Default sort within groups by orderInGroup
      groups[groupId].sort((a, b) => (a.orderInGroup || 0) - (b.orderInGroup || 0));
      return groups;
    }, {});
  }, [attributes]);

  // Get unique group IDs
  const groupIds = useMemo(() => {
    return Object.keys(attributesByGroup).map(Number).sort((a, b) => a - b);
  }, [attributesByGroup]);

  // Filter and sort attributes
  const processedAttributes = useMemo(() => {
    if (!attributes) return [];

    let filtered = attributes;

    // Filter by group
    if (filterGroup !== null) {
      filtered = filtered.filter((a: Attribute) => (a.groupId || 0) === filterGroup);
    }

    // Filter by search term (name)
    if (searchTerm) {
      filtered = filtered.filter((a: Attribute) => 
        a.name.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    // Sort - now handled by ScrollableTable
    return filtered;
  }, [attributes, filterGroup, searchTerm]);

  const handleSort = (column: string) => {
    if (sortColumn === column) {
      setSortDirection(prev => prev === 'asc' ? 'desc' : 'asc');
    } else {
      setSortColumn(column);
      setSortDirection('asc');
    }
  };

  const handleAddAttribute = useCallback(async (): Promise<void> => {
    if (!newAttribute.name.trim()) {
      toast.error("Attribute name cannot be empty.");
      return;
    }
     if (isNaN(newAttribute.groupId)) {
       toast.error("Group ID must be a valid number.");
       return;
    }
    if (isNaN(newAttribute.orderInGroup)) {
       toast.error("Order must be a valid number.");
       return;
    }

    try {
       const attributeToAdd: Omit<Attribute, 'id' | 'createdAt'> = {
        name: newAttribute.name.trim(),
        groupId: newAttribute.groupId,
        orderInGroup: newAttribute.orderInGroup
      };
      const newId = await addDocument(attributeToAdd);
      if (newId) {
        setNewAttribute({ name: "", groupId: 1, orderInGroup: 1 });
        setIsAddDialogOpen(false);
        toast.success(`Attribute "${attributeToAdd.name}" added successfully.`);
      } else {
         toast.error(`Failed to add attribute: ${addError?.message || 'Unknown error'}`);
      }
    } catch (error) {
      console.error("Failed to add attribute:", error);
      toast.error("Failed to add attribute");
    }
  }, [addDocument, newAttribute, addError]);

  const handleEditAttribute = useCallback(async (): Promise<void> => {
    if (!editingAttribute || !editingAttribute.id) return;
     if (!editingAttribute.name.trim()) {
      toast.error("Attribute name cannot be empty.");
      return;
    }
     if (isNaN(editingAttribute.groupId)) {
       toast.error("Group ID must be a valid number.");
       return;
    }
    if (isNaN(editingAttribute.orderInGroup)) {
       toast.error("Order must be a valid number.");
       return;
    }
    
    try {
       const success = await updateDocument(editingAttribute.id, {
        name: editingAttribute.name.trim(),
        groupId: editingAttribute.groupId,
        orderInGroup: editingAttribute.orderInGroup
      });
      
      if (success) {
        setEditingAttribute(null);
        toast.success(`Attribute "${editingAttribute.name}" updated successfully.`);
      } else {
         toast.error(`Failed to update attribute: ${updateError?.message || 'Unknown error'}`);
      }
    } catch (error) {
      console.error("Failed to update attribute:", error);
      toast.error("Failed to update attribute");
    }
  }, [updateDocument, editingAttribute, updateError]);

  // Updated delete attribute handler to use Redux dialog flow
  const handleDeleteAttribute = useCallback((attribute: Attribute) => {
    if (!attribute || !attribute.id) return;
    dispatch(startDeletionAssessment({ entityType: 'attribute', entityId: attribute.id }));
    // Attributes require impact assessment to show which drinks will be affected
    dispatch(assessDeletionImpact({ entityType: 'attribute', entityId: attribute.id }));
  }, [dispatch]);

  const handleDeletionDialogClose = useCallback(() => {
    dispatch(cancelInteraction());
  }, [dispatch]);

  const handleConfirmAttributeDeletion = useCallback(async () => {
    if (selectedEntity && selectedEntity.type === 'attribute' && selectedEntity.id) {
      try {
        await dispatch(deleteEntity({ entityType: 'attribute', entityId: selectedEntity.id })).unwrap();
        
        if (editingAttribute?.id === selectedEntity.id) {
          setEditingAttribute(null);
        }
      } catch (error: any) {
        // Error handling is now done by the dialog component
        throw error;
      }
    }
  }, [dispatch, selectedEntity, editingAttribute]);

  const getDeletionAssessmentState = useCallback(() => {
    if (deletionImpact.error) return 'error';
    if (deletionImpact.loading === 'pending') return 'loading';
    return 'completed';
  }, [deletionImpact.loading, deletionImpact.error]);

  // Create table columns configuration
  const tableColumns = useMemo(() => {
    const columns: Column<Attribute>[] = [];
    
    columns.push({
      id: 'groupId',
      header: 'Group',
      cell: (attribute) => editingAttribute?.id === attribute.id ? (
        <Input
          type="number"
          value={editingAttribute!.groupId || 0}
          onChange={(e) => setEditingAttribute({...editingAttribute!, groupId: parseInt(e.target.value) || 0})}
          disabled={!canManage}
          className="w-16"
        />
      ) : (
        attribute.groupId ?? 'N/A'
      ),
      sortable: true,
      minWidth: '100px'
    });

    columns.push({
      id: 'orderInGroup',
      header: 'Order',
      cell: (attribute) => editingAttribute?.id === attribute.id ? (
        <Input
          type="number"
          value={editingAttribute!.orderInGroup || 0}
          onChange={(e) => setEditingAttribute({...editingAttribute!, orderInGroup: parseInt(e.target.value) || 0})}
          disabled={!canManage}
          className="w-16"
        />
      ) : (
        attribute.orderInGroup ?? 'N/A'
      ),
      sortable: true,
      minWidth: '100px'
    });

    columns.push({
      id: 'name',
      header: 'Name',
      cell: (attribute) => editingAttribute?.id === attribute.id ? (
        <Input
          value={editingAttribute!.name}
          onChange={(e) => setEditingAttribute({...editingAttribute!, name: e.target.value})}
          disabled={!canManage}
        />
      ) : (
        attribute.name
      ),
      sortable: true,
      minWidth: '200px'
    });

    if (canManage) {
      columns.push({
        id: 'actions',
        header: 'Actions',
        cell: (attribute) => editingAttribute?.id === attribute.id ? (
          <div className="flex justify-end gap-2">
            <Button size="sm" variant="outline" onClick={handleEditAttribute} disabled={!canManage}>Save</Button>
            <Button size="sm" variant="ghost" onClick={() => setEditingAttribute(null)}>Cancel</Button>
          </div>
        ) : (
          <div className="flex justify-end gap-2">
            <Button 
              size="sm"
              variant="outline" 
              onClick={() => setEditingAttribute(attribute)}
              disabled={!canManage}
            >
              Edit
            </Button>
            <Button 
              size="sm"
              variant="destructive" 
              onClick={() => handleDeleteAttribute(attribute)}
              disabled={!canManage || !attribute.id}
            >
              Delete
            </Button>
          </div>
        ),
        sortable: false,
        minWidth: '180px'
      });
    }
    
    return columns;
  }, [editingAttribute, canManage, handleEditAttribute]);
  
  // Create sort config for the table
  const tableSortConfig: SortConfig<Attribute> = useMemo(() => ({
    column: sortColumn as keyof Attribute | null,
    direction: sortDirection
  }), [sortColumn, sortDirection]);
  
  // Empty message with filters applied
  const emptyMessage = useMemo(() => {
    let message = "No attributes found";
    if (searchTerm) message += ` for "${searchTerm}"`;
    if (filterGroup !== null) message += ` in Group ${filterGroup}`;
    return message + '.';
  }, [searchTerm, filterGroup]);

  const loading = collectionLoading || adding || updating || (operationLoading === 'pending' && interactionState === 'confirming_deletion');

  return (
    <>
    <Card className="h-full flex flex-col overflow-hidden">
      <CardHeader>
        <CardTitle>Soda Attributes ({attributes?.length || 0})</CardTitle>
        <CardDescription>Manage your soda attributes here</CardDescription>
      </CardHeader>
      <CardContent className="flex-1 flex flex-col overflow-hidden pb-6">
        <div className="flex justify-between items-start mb-4 gap-4">
          <div className="flex gap-2 items-end">
            <div>
              <Label htmlFor="groupFilter">Filter by Group</Label>
              <Select 
                value={filterGroup === null ? "all" : filterGroup.toString()} 
                onValueChange={(value) => {
                  setFilterGroup(value === "all" ? null : Number(value));
                }}
              >
                <SelectTrigger className="w-[180px]">
                  <SelectValue placeholder="Select group" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Groups</SelectItem>
                  {groupIds.map((group: number) => (
                    <SelectItem key={group} value={group.toString()}>
                      Group {group}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label htmlFor="searchTerm">Filter by Name</Label>
              <Input 
                id="searchTerm"
                placeholder="Search by name..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-[200px]"
              />
            </div>
          </div>
          
          <div className="flex gap-2">
            {canManage && (
              <>
                <Dialog open={isAddDialogOpen} onOpenChange={setIsAddDialogOpen}>
                  <DialogTrigger asChild>
                    <Button disabled={!canManage}>Add New Attribute</Button>
                  </DialogTrigger>
                  <DialogContent>
                    <DialogHeader>
                      <DialogTitle>Add New Attribute</DialogTitle>
                      <DialogDescription>
                        Fill in the details for the new soda attribute.
                      </DialogDescription>
                    </DialogHeader>
                    
                    <div className="grid gap-4 py-4">
                      <div className="grid grid-cols-4 items-center gap-4">
                        <Label htmlFor="name" className="text-right">Name</Label>
                        <Input 
                          id="name" 
                          value={newAttribute.name} 
                          onChange={(e) => setNewAttribute({...newAttribute, name: e.target.value})}
                          className="col-span-3"
                        />
                      </div>
                      <div className="grid grid-cols-4 items-center gap-4">
                        <Label htmlFor="groupId" className="text-right">Group</Label>
                        <Input 
                          id="groupId" 
                          type="number" 
                          value={newAttribute.groupId} 
                          onChange={(e) => setNewAttribute({...newAttribute, groupId: parseInt(e.target.value)})}
                          className="col-span-3"
                        />
                      </div>
                      <div className="grid grid-cols-4 items-center gap-4">
                        <Label htmlFor="orderInGroup" className="text-right">Order</Label>
                        <Input 
                          id="orderInGroup" 
                          type="number" 
                          value={newAttribute.orderInGroup} 
                          onChange={(e) => setNewAttribute({...newAttribute, orderInGroup: parseInt(e.target.value)})}
                          className="col-span-3"
                        />
                      </div>
                    </div>
                    
                    <DialogFooter>
                      <Button onClick={handleAddAttribute} disabled={!canManage}>Add Attribute</Button>
                    </DialogFooter>
                  </DialogContent>
                </Dialog>
              </>
            )}
          </div>
        </div>
        
        {loading && !processedAttributes?.length ? (
          <div className="flex-1 flex items-center justify-center">Loading attributes...</div>
        ) : (
          <ScrollableTable
            data={processedAttributes}
            columns={tableColumns}
            sortConfig={tableSortConfig}
            onSort={handleSort}
            emptyMessage={emptyMessage}
            height="calc(100vh - 370px)"
          />
        )}
      </CardContent>
    </Card>

    {/* Deletion Dialog */}
    {(interactionState === 'confirming_deletion' || interactionState === 'assessing_deletion') && selectedEntity?.type === 'attribute' && (
      <DeleteDialog
        isOpen={true}
        onClose={handleDeletionDialogClose}
        entityType={selectedEntity.type as EditableEntityType}
        entityName={deletionImpact.report?.primaryDocument.name || selectedEntity.id || 'Attribute'}
        assessmentState={getDeletionAssessmentState()}
        impactReport={deletionImpact.report}
        deletionState={operationLoading === 'pending' ? 'loading' : operationError ? 'error' : 'idle'}
        error={deletionImpact.error || operationError}
        onConfirm={handleConfirmAttributeDeletion}
      />
    )}
  </>
  );
}; 